/**
 * Whether a full-page navigation out of the SPA is under way.
 *
 * Two callers use it to stay quiet about work the navigation itself tore down:
 * the request wrapper reports an incomplete response as a cancellation rather
 * than a fault, and the router's error element swallows an aborted chunk.
 *
 * Optimistic by design - `beforeunload` also fires for navigations the user
 * then cancels, and for downloads - so only ever use it to suppress something
 * nobody would have seen, never to drive a side effect.
 */
let navigatingAway = false;
let graceTimer: number | undefined;

// long enough to cover a real navigation, short enough that a cancelled one
// does not leave this stuck on
const NAVIGATION_GRACE_MS = 5000;

const cancelGrace = () => {
  window.clearTimeout(graceTimer);
  graceTimer = undefined;
};

// fires as soon as a navigation starts, which is early enough to catch requests
// and chunks aborted on the way out
window.addEventListener("beforeunload", () => {
  navigatingAway = true;
  cancelGrace();
  graceTimer = window.setTimeout(() => {
    navigatingAway = false;
  }, NAVIGATION_GRACE_MS);
});

// the new document has committed
window.addEventListener("pagehide", () => {
  navigatingAway = true;
  cancelGrace();
});

// restored - the page is live again
window.addEventListener("pageshow", () => {
  navigatingAway = false;
  cancelGrace();
});

export const isNavigatingAway = () => navigatingAway;
